// Apolo IDE — ExecutionChat (S+A+B+C chat refresh).
//
// Container de scroll do Code tab. Estilo Claude Code:
//   • ConversationFeed agrupa mensagens em turnos (avatar 1x por turno).
//   • Autoscroll INTELIGENTE: só ancora no fim se o usuário já está no fim —
//     se ele rolou pra cima pra ler, não puxa (B1).
//   • Pílula "ir pro fim" flutuante quando há conteúdo novo abaixo (B1).
//   • Toolbar discreta: buscar na conversa (C2) + densidade (C3).
//   • Empty/loading states preservados.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDown, GitCommitVertical, Gauge, Loader2, MessageSquare, MessageSquareDashed, Palette, Rows2, Rows3, Rows4, Search, X } from 'lucide-react';
import ConversationFeed from './chat/ConversationFeed.jsx';
import ChatErrorBoundary from './chat/ChatErrorBoundary.jsx';
import TracePanel from './chat/TracePanel.jsx';
// Sprint tasking-cursor-ide P4-T03 — Task Board mounts inline above the
// conversation feed when the active run carries a DAG; absent slice = no-op.
import useChatToolbarState from '../../hooks/useChatToolbarState.js';

import { AGENT_RUNTIME_VERSION } from '../../lib/agentRuntimeVersion.js';
import { CHAT_GLOW_THEMES } from '../../lib/chatGlowTheme.js';
import { DENSITY_LABELS } from '../../lib/chatDensity.js';

const BOTTOM_THRESHOLD = 64; // px do fim que ainda conta como "no fim"

function prefersReducedMotion() {
  try { return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

export default function ExecutionChat({
  messages,
  loading,
  model,
  sessionId,
  toolbarLeading = null,
  toolbar: externalToolbar,
  hideToolbarIcons = false,
  // When false, the parent owns the scroll container (single authoritative
  // scroller — see SessionWorkspace, where the live StreamingMessage renders as a
  // SIBLING of this component). We then flow content at natural height and drop
  // our own inner scroller / follow logic / "Ir pro fim" button so they don't
  // fight the parent's stick-to-bottom. Default true keeps standalone behavior.
  manageScroll = true,
}) {
  const internalToolbar = useChatToolbarState();
  const t = externalToolbar ?? internalToolbar;
  const {
    density,
    view,
    searchOpen,
    query,
    setQuery,
    traceOpen,
    glowTheme,
    searchInputRef,
    toggleDensity,
    toggleView,
    toggleGlowTheme,
    closeSearch,
    openSearch,
    toggleTrace,
  } = t;

  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const atBottomRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);

  const recomputeBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const next = dist <= BOTTOM_THRESHOLD;
    atBottomRef.current = next;
    setAtBottom(next);
  }, []);

  // HRN3-T04 — autoscroll FOLLOWS streaming text. A ResizeObserver on the feed
  // content scrolls to bottom whenever it grows (token-by-token), not only when
  // the message COUNT changes — so the live answer stays in view as it streams.
  // Instant (not smooth) to keep pace with fast tokens; only when already at the
  // bottom, so it never steals scroll from a user reading above.
  useEffect(() => {
    if (!manageScroll) return undefined;
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (!atBottomRef.current) return;
      el.scrollTop = el.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [manageScroll]);

  function scrollToBottom() {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    atBottomRef.current = true;
    setAtBottom(true);
  }

  // The toolbar hosts the per-session agents indicator (toolbarLeading). It must
  // stay visible in the loading/empty states too — a brand-new session can
  // dispatch a workflow as its first action, before any message exists.
  const leadingBar = toolbarLeading ? (
    <div className="flex items-center px-1 pb-1">{toolbarLeading}</div>
  ) : null;

  if (loading && messages.length === 0) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        {leadingBar}
        <div role="status" aria-live="polite" className="flex flex-1 items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin text-apolo-textTertiary" aria-hidden="true" />
          <span className="text-sm text-apolo-textTertiary">Carregando histórico…</span>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        {leadingBar}
        <div
          className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center"
          style={{ background: 'radial-gradient(ellipse 50% 60% at 50% 40%, rgba(59,130,246,0.04) 0%, transparent 100%)' }}
        >
          <div className="rounded-full bg-apolo-surface2/60 p-3">
            <MessageSquareDashed size={22} className="text-apolo-textTertiary" aria-hidden="true" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-apolo-textSecondary">Nenhuma mensagem ainda</p>
            <p className="max-w-xs text-xs text-apolo-textTertiary">
              Digite uma instrução abaixo. Comandos viram cartões colapsáveis; código ganha syntax highlight.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const DensityIcon = density === 'compact' ? Rows2 : density === 'detailed' ? Rows4 : Rows3;
  const densityLabel = DENSITY_LABELS[density] || 'Confortável';
  const showToolbarRow = toolbarLeading || searchOpen || !hideToolbarIcons;

  return (
    <div className={manageScroll ? 'relative flex min-h-0 flex-1 flex-col' : 'flex flex-col'}>
      {showToolbarRow ? (
        <div className="flex items-center gap-1 px-1 pb-1">
          {toolbarLeading}
          {searchOpen ? (
            <div className="flex flex-1 items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1">
              <Search size={13} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />
              <input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
                placeholder="Buscar… author:user type:tool date:2026-06-01"
                aria-label="Buscar na conversa"
                className="min-w-0 flex-1 bg-transparent text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
              />
              <button
                type="button"
                onClick={closeSearch}
                aria-label="Fechar busca"
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
              >
                <X size={13} aria-hidden="true" />
              </button>
            </div>
          ) : !hideToolbarIcons ? (
            <>
              <span
                className="ml-1 hidden shrink-0 font-mono text-[9px] tabular-nums text-apolo-textDisabled sm:inline"
                title="Versão do agent loop — confirme que o rebuild carregou"
              >
                {AGENT_RUNTIME_VERSION}
              </span>
              <button
                type="button"
                onClick={openSearch}
                aria-label="Buscar na conversa"
                title="Buscar na conversa"
                className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
              >
                <Search size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={toggleView}
                aria-label={`Visão: ${view === 'timeline' ? 'timeline' : 'conversa'} — alternar`}
                title={`Visão: ${view === 'timeline' ? 'timeline' : 'conversa'}`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
              >
                {view === 'timeline' ? <GitCommitVertical size={14} aria-hidden="true" /> : <MessageSquare size={14} aria-hidden="true" />}
              </button>
              <button
                type="button"
                onClick={toggleTrace}
                aria-label="Trace de execução — timing e tokens por turno"
                aria-pressed={traceOpen}
                title="Trace (timing + tokens por turno)"
                className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40 ${
                  traceOpen
                    ? 'bg-apolo-surface2 text-apolo-accent'
                    : 'text-apolo-textTertiary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
                }`}
              >
                <Gauge size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={toggleGlowTheme}
                aria-label={`Tema de glow: ${CHAT_GLOW_THEMES[glowTheme]?.label || 'Ouro'} — alternar`}
                title={`Glow: ${CHAT_GLOW_THEMES[glowTheme]?.label || 'Ouro'}`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
              >
                <Palette size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={toggleDensity}
                aria-label={`Densidade: ${densityLabel} — alternar`}
                title={`Densidade: ${densityLabel}`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
              >
                <DensityIcon size={14} aria-hidden="true" />
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* E2-T02 — persistent per-turn TRACE (read-only). Non-intrusive: a
          collapsible glass panel above the live feed; opening it never disturbs
          the conversation. Mounts (and fetches) only while open. */}
      {traceOpen && sessionId && (
        <div className="mb-2 max-h-[42%] overflow-y-auto px-1">
          <TracePanel sessionId={sessionId} enabled={traceOpen} />
        </div>
      )}

      {/* HRN3-T03 — the scroll container is NOT a live region. Announcing every
          appended token/message here spammed screen readers (and duplicated the
          LiveActivity announcer). The ONE dedicated polite announcer lives in
          LiveActivity (phase only); this container is just scroll. */}
      <div
        ref={scrollRef}
        onScroll={manageScroll ? recomputeBottom : undefined}
        data-density={density}
        className={`${manageScroll ? 'flex-1 overflow-y-auto ' : ''}pb-2 ${density === 'compact' ? 'px-0 rx-density-compact' : 'px-1 pb-3'}`}
      >
        <div ref={contentRef}>
          {/* hgmq-finish — the task DAG now lives ONLY in the right-rail
              TasksPanel (pos-s5 idiom), so the inline TaskBoard above the feed
              is removed to declutter the center column (no duplication). */}
          <ChatErrorBoundary resetKey={messages.length}>
            <ConversationFeed messages={messages} model={model} density={density} query={searchOpen ? query : ''} sessionId={sessionId} render={view} />
          </ChatErrorBoundary>
        </div>
      </div>

      {manageScroll && !atBottom && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-apolo-borderSubtle bg-apolo-surface2/95 px-3 py-1.5 text-[12px] font-medium text-apolo-textSecondary shadow-lg backdrop-blur transition hover:border-apolo-accent/40 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
        >
          <ArrowDown size={13} aria-hidden="true" />
          Ir pro fim
        </button>
      )}
    </div>
  );
}
